<template>
  <div class="container mx-auto px-4 py-8 max-w-6xl">
    <div class="flex justify-between items-center mb-6">
      <div>
        <h1 class="text-3xl font-bold text-gray-900">공지사항</h1>
        <p class="mt-2 text-gray-600">중요한 공지사항을 확인하세요</p>
      </div>
      
      <!-- 관리자만 작성 가능 -->
      <div v-if="authStore.isAdmin" class="flex gap-2">
        <RouterLink
          to="/notices/create"
          class="btn btn-primary btn-sm"
        >
          <PlusIcon class="w-5 h-5 mr-2" />
          공지사항 작성
        </RouterLink>
      </div>
    </div>

    <BoardSearchBar
      v-model="searchQuery"
      placeholder="제목, 내용으로 검색..."
      :show-button="true"
      :disabled="loading"
      @search="search"
    />

    <div class="mt-4 mb-4">
      <BoardListToolbar
        v-model:pageSize="pageSize"
        :page-size-options="pageSizeOptions"
        :total-elements="totalElements"
        count-label="공지사항"
        :show-page-size="true"
        @page-size-change="handlePageSizeChange"
      />
    </div>

    <!-- 공지사항 목록 -->
    <div class="bg-white rounded-lg shadow-sm border">
      <div v-if="loading" class="p-8 text-center">
        <div class="spinner w-8 h-8 mx-auto mb-4"></div>
        <p class="text-gray-600">공지사항을 불러오는 중...</p>
      </div>

      <div v-else-if="notices.length === 0" class="p-12 text-center">
        <DocumentTextIcon class="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 class="text-lg font-medium text-gray-900 mb-2">공지사항이 없습니다</h3>
        <p class="text-gray-600">첫 번째 공지사항을 작성해보세요.</p>
      </div>

      <div v-else>
        <!-- 헤더 -->
        <div class="border-b border-gray-200 px-6 py-3 bg-gray-50">
          <div class="grid grid-cols-12 gap-4 text-sm font-medium text-gray-700">
            <div class="col-span-6">제목</div>
            <div class="col-span-2">작성자</div>
            <div class="col-span-2">작성일</div>
            <div class="col-span-2 text-center">관리</div>
          </div>
        </div>

        <!-- 목록 -->
        <div class="divide-y divide-gray-200">
          <div
            v-for="notice in notices"
            :key="notice.id"
            class="px-6 py-4 hover:bg-gray-50 transition-colors"
          >
            <div class="grid grid-cols-12 gap-4 items-center">
              <div class="col-span-6">
                <RouterLink
                  :to="`/notices/${notice.id}`"
                  class="text-primary-600 hover:text-primary-700 font-medium"
                >
                  {{ notice.title }}
                </RouterLink>
              </div>
              <div class="col-span-2 text-sm text-gray-600">
                {{ notice.author.name }}
              </div>
              <div class="col-span-2 text-sm text-gray-600">
                {{ formatDate(notice.createdAt) }}
              </div>
              <div class="col-span-2 text-center">
                <div v-if="authStore.isAdmin" class="flex justify-center gap-2">
                  <RouterLink
                    :to="`/notices/${notice.id}/edit`"
                    class="text-blue-600 hover:text-blue-700 text-sm"
                  >
                    <PencilIcon class="w-4 h-4" />
                  </RouterLink>
                  <button
                    @click="deleteNotice(notice.id)"
                    class="text-red-600 hover:text-red-700 text-sm"
                  >
                    <TrashIcon class="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="totalPages > 1" class="mt-8">
      <Pagination :current-page="currentPage" :total-pages="totalPages" @page-change="changePage" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { RouterLink } from 'vue-router'
import {
  PlusIcon,
  DocumentTextIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/vue/24/outline'
import { useAuthStore } from '@/stores/auth'
import { noticeApi } from '@/services/api'
import type { Notice } from '@/types'
import { useToast } from 'vue-toastification'
import Pagination from '@/components/common/Pagination.vue'
import BoardSearchBar from '@/components/common/BoardSearchBar.vue'
import BoardListToolbar from '@/components/common/BoardListToolbar.vue'

const authStore = useAuthStore()
const toast = useToast()

const notices = ref<Notice[]>([])
const loading = ref(false)
const searchQuery = ref('')
const currentPage = ref(0)
const pageSizeOptions = [6, 12, 18, 21]
const pageSize = ref(6)
const totalElements = ref(0)
const totalPages = ref(0)

const formatDate = (dateString: string) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('ko-KR')
}

const loadNotices = async () => {
  try {
    loading.value = true
    const response = await noticeApi.getNotices({
      keyword: searchQuery.value || undefined,
      page: currentPage.value,
      size: pageSize.value
    })
    
    notices.value = response.content
    totalElements.value = response.totalElements
    totalPages.value = response.totalPages
  } catch (error: any) {
    console.error('Failed to load notices:', error)
    toast.error('공지사항 목록을 불러오는데 실패했습니다.')
  } finally {
    loading.value = false
  }
}

const search = () => {
  currentPage.value = 0
  loadNotices()
}

const changePage = (page: number) => {
  currentPage.value = page
  loadNotices()
}

const handlePageSizeChange = () => {
  currentPage.value = 0
  loadNotices()
}

const deleteNotice = async (id: number) => {
  if (!confirm('정말 삭제하시겠습니까?')) return

  try {
    await noticeApi.deleteNotice(id)
    toast.success('공지사항이 삭제되었습니다.')
    loadNotices()
  } catch (error: any) {
    console.error('Failed to delete notice:', error)
    toast.error('공지사항 삭제에 실패했습니다.')
  }
}

watch(searchQuery, () => {
  if (searchQuery.value === '') {
    search()
  }
})

onMounted(() => {
  loadNotices()
})
</script>
